import { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Carousel from 'nuka-carousel';

import type { ListingImage } from 'hooks/useDirectoryData/types.d';

import c from './_profileGallery.module.scss';

export interface ProfileGalleryProps {
  images: ListingImage[];
  propertyName: string;
}

export function ProfileGallery({ images, propertyName }: ProfileGalleryProps) {
  const [current, setCurrent] = useState(0);

  const total = images.length;
  const showControls = total > 1;

  const goTo = (index: number) => {
    setCurrent(((index % total) + total) % total);
  };

  return (
    <div className={clsx('listing-gallery', c.gallery)}>
      <div className={clsx('listing-gallery-frame', c.frame)}>
        {/* @ts-expect-error nuka-carousel accepts children; type defs omit it */}
        <Carousel
          slideIndex={current}
          afterSlide={setCurrent}
          wrapAround
          transitionMode="fade"
          renderBottomCenterControls={null}
          renderCenterLeftControls={
            showControls
              ? () => {
                  return (
                    <button
                      type="button"
                      className={clsx('listing-gallery-nav prev', c.nav, c.navPrev)}
                      aria-label="Previous photo"
                      onClick={() => {
                        goTo(current - 1);
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'chevron-left']} />
                    </button>
                  );
                }
              : null
          }
          renderCenterRightControls={
            showControls
              ? () => {
                  return (
                    <button
                      type="button"
                      className={clsx('listing-gallery-nav next', c.nav, c.navNext)}
                      aria-label="Next photo"
                      onClick={() => {
                        goTo(current + 1);
                      }}
                    >
                      <FontAwesomeIcon icon={['fas', 'chevron-right']} />
                    </button>
                  );
                }
              : null
          }
          renderBottomRightControls={
            showControls
              ? () => {
                  return (
                    <div className={clsx('listing-gallery-count', c.count)}>
                      {current + 1}
                      {' / '}
                      {total}
                    </div>
                  );
                }
              : null
          }
        >
          {images.map((image, index) => {
            return (
              <img
                key={image.url}
                src={image.url}
                alt={`${propertyName} — photo ${index + 1}`}
                loading={index === 0 ? 'eager' : 'lazy'}
                className={clsx('listing-gallery-slide__img', c.slideImg)}
              />
            );
          })}
        </Carousel>
      </div>

      {showControls && (
        <div className={clsx('listing-gallery-dots', c.dots)}>
          {images.map((image, index) => {
            return (
              <button
                type="button"
                key={image.url}
                className={clsx('listing-gallery-dot', c.dot, {
                  'is-active': index === current,
                  [c.isActive]: index === current,
                })}
                aria-label={`Go to photo ${index + 1}`}
                onClick={() => {
                  goTo(index);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
